import { SnapStyleAPIClient } from "./api-client";
import { heicTo } from "heic-to";
import { SnapStyleConfig } from "./types";
import { ModalManager } from "./modal";
import { PhotoManager } from "./photo-manager";
import { TryOnManager } from "./tryon-manager";

export class SnapStyleWidget {
  private static readonly API_KEY_WAIT_MS = 10000;
  private client: SnapStyleAPIClient;
  private config: SnapStyleConfig | null = null;
  private button: HTMLButtonElement | null = null;
  private isButtonInitialized = false;
  private isOpeningModal = false;
  private modalManager: ModalManager;
  private photoManager: PhotoManager;
  private tryOnManager: TryOnManager;

  // Current flow state
  private selectedFile: File | null = null;

  constructor() {
    const apiUrl =
      process.env["SNAPSTYLE_API_URL"] || "https://api.snapstyle.ai";

    // Extract API key from DOM config (best-effort at construction time;
    // resolveApiKey() in hydrate() will retry via MutationObserver if not yet present)
    const configDiv = document.getElementById('snapstyle-config');
    const apiKey = configDiv?.dataset['config'] || '';

    this.client = new SnapStyleAPIClient(apiUrl, apiKey);
    this.modalManager = new ModalManager();
    this.photoManager = new PhotoManager(this.client);
    this.tryOnManager = new TryOnManager(this.client);
  }

  async hydrate(): Promise<void> {
    const apiKeyResolution = this.resolveApiKey();
    void apiKeyResolution.then((resolvedKey) => {
      if (resolvedKey && this.button && this.isButtonInitialized) {
        void this.enforceWidgetAvailability();
      }
    });

    this.button = document.querySelector("[data-snapstyle-widget]");

    if (!this.button) {
      this.waitForButton();
      return;
    }

    await this.initializeButton();
  }

  /**
   * Ensures the API key is set on the client.
   * If #snapstyle-config is already in the DOM, sets the key immediately.
   * Otherwise, watches for it via MutationObserver (handles late-rendered divs).
   */
  private async resolveApiKey(timeoutMs = 10000): Promise<string | null> {
    const configDiv = document.getElementById('snapstyle-config');
    const key = configDiv?.dataset['config'];
    if (key) {
      this.client.setApiKey(key);
      return key;
    }

    return await new Promise<string | null>((resolve) => {
      const observer = new MutationObserver(() => {
        const div = document.getElementById('snapstyle-config');
        const resolvedKey = div?.dataset['config'];
        if (resolvedKey) {
          this.client.setApiKey(resolvedKey);
          observer.disconnect();
          clearTimeout(timeout);
          resolve(resolvedKey);
        }
      });

      const timeout = window.setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, timeoutMs);

      observer.observe(document.body, { childList: true, subtree: true });
    });
  }

  private waitForButton(): void {
    const timeout = setTimeout(() => {
      console.warn("SnapStyle: Widget button not found after 10 seconds");
      observer.disconnect();
    }, 10000);

    const observer = new MutationObserver(() => {
      this.button = document.querySelector("[data-snapstyle-widget]");
      if (this.button) {
        clearTimeout(timeout);
        observer.disconnect();
        void this.initializeButton();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private async initializeButton(): Promise<void> {
    if (!this.button || this.isButtonInitialized) return;

    this.config = this.extractConfigFromButton();
    if (!this.config) return;

    const allowed = await this.enforceWidgetAvailability();
    if (!allowed) return;

    this.button.addEventListener("click", (e) => {
      void this.openModal(e);
    });
    this.isButtonInitialized = true;
  }

  private removeTryOnButton(reason?: string | null): void {
    if (!this.button) return;

    const storeHint = this.config?.shopDomain ? ` store=${this.config.shopDomain}` : "";
    const reasonText = reason ? ` reason=${reason}` : "";
    console.info(`[SnapStyle] Hiding Try On button.${storeHint}${reasonText}`);

    this.button.remove();
    this.button = null;
    this.isButtonInitialized = false;
  }

  private async enforceWidgetAvailability(): Promise<boolean> {
    const key = await this.resolveApiKey(SnapStyleWidget.API_KEY_WAIT_MS);
    if (!key) {
      console.warn("[SnapStyle] API key not found; skipping widget availability gate");
      return true;
    }

    try {
      const availability = await this.client.getWidgetAvailability();
      if (!availability.allowed) {
        this.removeTryOnButton(availability.reason ?? undefined);
        return false;
      }
      return true;
    } catch (error) {
      console.warn(
        "[SnapStyle] Widget availability check failed; keeping button visible.",
        error,
      );
      return true;
    }
  }

  /**
   * Returns the product image URL to use for this try-on.
   *
   * Always the data-product-image attribute the merchant's template set for
   * this button — the only value guaranteed to belong to this product.
   *
   * Variant-aware image detection (using the customer's actually-selected
   * variant, e.g. Blue instead of the default Red) is not supported yet —
   * planned for a future version.
   */
  private getEffectiveProductImageUrl(): string {
    return this.config?.productImageUrl || "";
  }

  private extractConfigFromButton(): SnapStyleConfig | null {
    if (!this.button) return null;

    const {
      shop,
      productId,
      productTitle,
      productImage,
      variantId,
      category,
      productType,
      instructions,
    } = this.button.dataset;

    if (!productId) {
      console.error("SnapStyle: Missing required productId data attribute");
      return null;
    }

    if (!shop) {
      console.warn("SnapStyle: Missing shop data attribute. Try-on attribution may not work correctly.");
    }

    const currentHostname =
      typeof window !== "undefined" && window.location
        ? window.location.hostname
        : "";

    return {
      shopDomain: shop || currentHostname || "",
      productId,
      productTitle: productTitle || "Product",
      productImageUrl: productImage || "",
      variantId,
      category: category || productType || instructions,
    };
  }

  /* ───────────────────────────────
     Modal Flow
     ─────────────────────────────── */

  private async openModal(e: Event): Promise<void> {
    e.preventDefault();
    if (!this.button || !this.config || this.isOpeningModal || this.modalManager.isOpen) return;

    this.isOpeningModal = true;

    const originalText = this.button.textContent;
    this.button.textContent = "Loading...";
    this.button.disabled = true;

    try {
      const allowed = await this.enforceWidgetAvailability();
      if (!allowed || !this.button) {
        return;
      }

      await this.client.ensureVisitorSession();

      // Load history for the upload screen
      const history = await this.tryOnManager.loadHistory();

      this.modalManager.create(
        {
          onClose: () => this.closeModal(),
          onBrowseGallery: () => this.handleBrowseGallery(),
          onTakePhoto: () => this.handleTakePhoto(),
          onTryOn: () => this.handleTryOn(),
          onChooseAnother: () => this.handleChooseAnother(),
          onRetry: () => this.handleRetry(),
          onAddToCart: () => this.handleAddToCart(),
          onHistoryItemClick: (resultUrl, originalUrl) =>
            this.handleHistoryItemClick(resultUrl, originalUrl),
          onViewAllHistory: () => this.modalManager.showScreen("history"),
        },
        this.config.shopDomain,
      );

      this.modalManager.setTryOnHistory(history);
      this.modalManager.showScreen("upload");
    } catch (error) {
      console.error("Error:", error);
      this.modalManager.showErrorToast(
        "Failed to open try-on. Please try again.",
        () => this.openModal(new Event("click")),
      );
      this.modalManager.destroy();
    } finally {
      this.isOpeningModal = false;
      if (this.button) {
        this.button.textContent = originalText;
        this.button.disabled = false;
      }
    }
  }

  private closeModal(): void {
    this.modalManager.destroy();
    this.resetFlowState();
  }

  private resetFlowState(): void {
    this.selectedFile = null;
  }

  /* ───────────────────────────────
     Upload Screen Handlers
     ─────────────────────────────── */

  // Image constraints matching backend
  private static readonly MAX_IMAGE_SIZE_MB = 10;
  private static readonly ALLOWED_IMAGE_TYPES = new Set([
    "image/jpeg",
    "image/png",
    "image/webp",
    "image/heic",
    "image/heif",
  ]);
  private static readonly ALLOWED_IMAGE_EXTENSIONS = new Set([
    ".jpg",
    ".jpeg",
    ".png",
    ".webp",
    ".heic",
    ".heif",
  ]);
  private static readonly MAX_IMAGE_DIMENSION = 6144;

  private getFileExtension(filename: string): string {
    const dotIndex = filename.lastIndexOf(".");
    if (dotIndex < 0) return "";
    return filename.slice(dotIndex).toLowerCase();
  }

  private isHeicFile(file: File): boolean {
    const extension = this.getFileExtension(file.name);
    return (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      extension === ".heic" ||
      extension === ".heif"
    );
  }

  private isAllowedImageFile(file: File): boolean {
    return (
      SnapStyleWidget.ALLOWED_IMAGE_TYPES.has(file.type) ||
      SnapStyleWidget.ALLOWED_IMAGE_EXTENSIONS.has(this.getFileExtension(file.name))
    );
  }

  private async normalizeSelectedImage(file: File): Promise<File> {
    if (!this.isHeicFile(file)) {
      return file;
    }

    try {
      const convertedBlob = await heicTo({
        blob: file,
        type: "image/jpeg",
        quality: 0.92,
      });
      const baseName = file.name.replace(/\.(heic|heif)$/i, "") || "upload";
      return new File([convertedBlob], `${baseName}.jpg`, {
        type: "image/jpeg",
        lastModified: file.lastModified,
      });
    } catch {
      throw new Error("Could not convert this HEIC image. Please choose a different photo.");
    }
  }

  /**
   * Validates an image file against backend constraints.
   * Returns an error message string, or null if valid.
   */
  private async validateImage(file: File): Promise<string | null> {
    // Check file type
    if (!this.isAllowedImageFile(file)) {
      return "Please upload a JPEG, PNG, WebP, or HEIC image.";
    }

    // Check file size
    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > SnapStyleWidget.MAX_IMAGE_SIZE_MB) {
      return `Image is too large (${sizeMB.toFixed(1)}MB). Please upload an image under ${SnapStyleWidget.MAX_IMAGE_SIZE_MB}MB.`;
    }

    // Check dimensions
    try {
      const dimensions = await this.getImageDimensions(file);
      if (dimensions.width > SnapStyleWidget.MAX_IMAGE_DIMENSION ||
        dimensions.height > SnapStyleWidget.MAX_IMAGE_DIMENSION) {
        return `Image dimensions are too large (${dimensions.width}×${dimensions.height}px). Maximum allowed is ${SnapStyleWidget.MAX_IMAGE_DIMENSION}×${SnapStyleWidget.MAX_IMAGE_DIMENSION}px.`;
      }
    } catch {
      return "Could not read image. Please try a different file.";
    }

    return null;
  }

  private async prepareSelectedImage(file: File): Promise<File> {
    const normalizedFile = await this.normalizeSelectedImage(file);
    const error = await this.validateImage(normalizedFile);
    if (error) {
      throw new Error(error);
    }
    return normalizedFile;
  }

  private getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Failed to load image"));
      };
      img.src = url;
    });
  }

  private handleBrowseGallery(): void {
    const input = document.getElementById("snapstyle-gallery-input") as HTMLInputElement;
    if (!input) return;

    input.value = "";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          const preparedFile = await this.prepareSelectedImage(file);
          await this.showPhotoPreview(preparedFile);
        } catch (error) {
          const message = error instanceof Error ? error.message : "Please choose a different image.";
          this.modalManager.showErrorToast(message);
          return;
        }
      }
    };
    input.click();
  }

  private handleTakePhoto(): void {
    const input = document.getElementById("snapstyle-camera-input") as HTMLInputElement;
    if (!input) return;

    input.value = "";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (file) {
        try {
          const preparedFile = await this.prepareSelectedImage(file);
          await this.showPhotoPreview(preparedFile);
        } catch (error) {
          const message = error instanceof Error ? error.message : "Please choose a different image.";
          this.modalManager.showErrorToast(message);
          return;
        }
      }
    };
    input.click();
  }

  private async showPhotoPreview(file: File): Promise<void> {
    this.selectedFile = file;

    try {
      const dataUrl = await this.photoManager.getPreviewDataUrl(file);
      this.modalManager.setPreviewImage(dataUrl);
      this.modalManager.showScreen("preview");
    } catch (error) {
      console.error("Error previewing photo:", error);
    }
  }

  /* ───────────────────────────────
     Preview Screen Handlers
     ─────────────────────────────── */

  private handleChooseAnother(): void {
    this.selectedFile = null;
    this.modalManager.showScreen("upload");
  }

  private async handleTryOn(): Promise<void> {
    if (!this.selectedFile || !this.config) return;

    // Show upload-specific loader while the photo is being sent to the server
    this.modalManager.showUploadLoader();

    const productImageUrl = this.getEffectiveProductImageUrl();

    try {
      // Upload the photo
      const photo = await this.photoManager.uploadPhoto(this.selectedFile);

      // Photo uploaded — switch to the try-on processing loader
      this.modalManager.showScreen("loading");

      // Set the original photo URL for compare view
      this.modalManager.setOriginalPhotoUrl(photo.storageUrl);

      const effectiveConfig = productImageUrl
        ? { ...this.config!, productImageUrl }
        : this.config!;

      const resultUrl = await this.tryOnManager.processTryOn(
        photo.id,
        effectiveConfig,
        (percent) => this.modalManager.updateProgress(percent),
      );

      // Reload history
      const history = await this.tryOnManager.loadHistory();
      this.modalManager.setTryOnHistory(history);

      // Show result
      this.modalManager.setResultImage(resultUrl, true);
      this.modalManager.showScreen("result");
    } catch (error) {
      console.error("Try-on error:", error);
      // Go back to upload screen with error
      this.modalManager.showScreen("upload");
      const msg = error instanceof Error && error.message.includes('network')
        ? "Network issue detected. Please check your connection and try again."
        : "Something went wrong with the try-on generation. Please try again.";
      this.modalManager.showErrorToast(msg, () => this.handleTryOn());
    }
  }

  /* ───────────────────────────────
     Result Screen Handlers
     ─────────────────────────────── */

  private handleRetry(): void {
    this.resetFlowState();
    this.modalManager.showScreen("upload");
  }

  /**
   * Triggers the platform's native add-to-cart action.
   *
   * Strategy (in priority order):
   * 1. Click the native add-to-cart button already on the page.
   *    This works universally: Shopify (respects cart drawers/Ajax),
   *    WooCommerce, BigCommerce, and Magento all have a form submit button.
   * 2. If no button is found, fall back to Shopify's /cart/add.js AJAX API
   *    using the variant ID from the live form hidden input (not the stale config).
   */
  private handleAddToCart(): void {
    // Close the modal first so the user sees the cart update happen on the page
    this.closeModal();

    // ── Step 1: click the native add-to-cart button ──────────────────────────
    // Ordered by specificity. All major platforms have one of these selectors.
    const NATIVE_BTN_SELECTORS = [
      // Shopify (Dawn / Debut / Brooklyn / any Shopify theme)
      'button[name="add"]',
      '.product-form__submit',
      '#AddToCart',
      // WooCommerce
      '.single_add_to_cart_button',
      'button.cart',
      // BigCommerce
      '#form-action-addToCart',
      '.add-to-cart-button',
      // Magento 2
      '#product-addtocart-button',
      '.action.tocart',
    ];

    for (const sel of NATIVE_BTN_SELECTORS) {
      const btn = document.querySelector<HTMLButtonElement>(sel);
      if (btn && !btn.disabled) {
        btn.click();
        return;
      }
    }

    // ── Step 2: Shopify AJAX fallback ────────────────────────────────────────
    // Read the variant ID from the form's live hidden input so we always use
    // the currently selected variant, not what was set at widget init time.
    const variantInput = document.querySelector<HTMLInputElement>(
      'input[name="id"].product-variant-id, input[name="id"][form*="product"]'
    );
    const variantId = variantInput?.value || this.config?.variantId;

    if (variantId && typeof fetch !== "undefined") {
      const id = parseInt(variantId, 10);
      if (!isNaN(id)) {
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: [{ id, quantity: 1 }] }),
        }).catch((err) => {
          console.warn("[SnapStyle] Add to cart AJAX failed:", err);
        });
      }
    } else {
      console.warn("[SnapStyle] Add to cart: no native button found and no variant ID available.");
    }
  }

  private handleHistoryItemClick(resultUrl: string, originalUrl: string): void {
    this.modalManager.setResultImage(resultUrl);
    this.modalManager.setOriginalPhotoUrl(originalUrl);
    this.modalManager.showScreen("result");
  }
}

// Auto-initialize on page load (singleton)
if (typeof window !== "undefined") {
  const w = window as unknown as { __snapstyleWidget?: SnapStyleWidget };

  if (!w.__snapstyleWidget) {
    const initialize = () => {
      w.__snapstyleWidget = new SnapStyleWidget();
      void w.__snapstyleWidget.hydrate();
    };

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", initialize);
    } else {
      initialize();
    }
  }
}
